import { useAuth } from '@/contexts/shared/AuthContext';
import { JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CHANGE_PASSWORD_PATH, mustChangePassword } from '@/auth/passwordChange';
import { canOpenCreatorPage } from '@/config/adminAccess';
import { useScopedCapabilities } from '@/hooks/useScopedCapabilities';

import { tr } from '@/i18n';
interface PrivateRouteProps {
  children: JSX.Element;
}

const PrivateRoute = ({ children }: PrivateRouteProps): JSX.Element => {
  const { user, isAuthenticated, isLoading, isImpersonating } = useAuth();
  const location = useLocation();
  // Creators who aren't administrators (a Course Creator on some categories only, a program manager, a community
  // creator) may open the admin pages for what they create — see canOpenCreatorPage.
  const creatorPath = location.pathname.startsWith('/admin') && !isImpersonating;
  const { data: caps, isLoading: capsLoading } = useScopedCapabilities(isAuthenticated && creatorPath);

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>{tr("ui.loading", "Loading...")}</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // An administrator asked for a new password: nothing else opens until it is chosen
  // (not while an admin is acting as this person).
  if (!isImpersonating && mustChangePassword(user)) {
    return <Navigate to={CHANGE_PASSWORD_PATH} state={{ from: location }} replace />;
  }

  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    if (isImpersonating) {
      return <Navigate to="/user/dashboard" replace />;
    }

    const hasAdminAccess = user?.roles?.some((role) => {
      const name = role.name.toLowerCase();
      return (
        name === 'superadmin' ||
        name === 'tenantadmin' ||
        name === 'site_admin' ||
        name === 'tenant_admin' ||
        // UAT DEF-159: business admins administer their tenants too.
        name === 'business_admin'
      );
    });
    // UAT DEF-159: course-scoped editors (editing trainer on a course, course
    // creator) reach the builder and the per-course management pages from the
    // course page's "Edit" control; the API enforces their course rights.
    const isCourseScopedAdminPage = /^\/admin\/(learning\/courses\/build\/\d+|courses\/\d+\/)/.test(location.pathname);

    if (!hasAdminAccess && !isCourseScopedAdminPage && !canOpenCreatorPage(caps, location.pathname)) {
      if (capsLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>{tr("ui.loading", "Loading...")}</div>;
      }
      return <Navigate to="/user/dashboard" replace />;
    }
  }

  return children;
};

export default PrivateRoute;